import React, { useMemo } from 'react'
import DashboardSectionCard from '@components/pages/Dashboard/DashboardSectionCard.component'
import DashboardStackedBarChart from '@components/pages/Dashboard/MetricCharts/DashboardStackedBarChart.component'
import DashboardSectionEmptyState from '@components/pages/Dashboard/DashboardSectionEmptyState.component'
import type { UseARAgingResult } from '@hooks/dashboard/useARAging'
import type { RangeKey } from '@dataTypes/dashboard.types'
import {
  AR_BUCKET_COLORS,
  FINANCE_AR_AGING_BAR_SIZE,
} from '@constants/Dashboard/financeDashboard.constants'
import {
  formatChartCurrencyDollars,
  formatCurrency,
  HYGIENE_PLACEHOLDER,
} from '@utils/dashboardFinance.util'
import {
  isDashboardSectionPending,
  shouldShowDashboardSectionEmpty,
} from '@utils/dashboardSectionEmpty.util'
import {
  FinanceArAgingBarLegend,
  FinanceMetricTilesRow,
} from '../../FinanceMetricTiles.component'

/** Figma 5462:119571 — 100px bars with gap between aging buckets. */
const AR_AGING_BAR_CHART_PROPS = {
  barSize: FINANCE_AR_AGING_BAR_SIZE,
  barCategoryGap: '18%',
} as const

type ARAgingCardProps = {
  titleHref: string
  range: RangeKey
  arAging: UseARAgingResult
}

export function ARAgingCard({
  titleHref,
  arAging,
}: ARAgingCardProps): React.ReactElement {
  const { loading, totals, chartData, arValueAxis, hasData, hasPayload } =
    arAging

  const barChartData = useMemo(
    () =>
      chartData.map((row) => ({
        label: row.label,
        value: row.insurance + row.patient,
      })),
    [chartData],
  )

  const metricTiles = useMemo(() => {
    if (loading || !totals) {
      return [
        { id: 'total-ar', leftLabel: 'Total AR', value: HYGIENE_PLACEHOLDER },
        {
          id: 'insurance-ar',
          leftLabel: 'Insurance AR',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'patient-ar',
          leftLabel: 'Patient AR',
          value: HYGIENE_PLACEHOLDER,
        },
        {
          id: 'overdue-ar',
          leftLabel: 'Overdue AR',
          value: HYGIENE_PLACEHOLDER,
        },
      ]
    }
    return [
      {
        id: 'total-ar',
        leftLabel: 'Total AR',
        value: formatCurrency(totals.totalAR.current, { compact: true }),
      },
      {
        id: 'insurance-ar',
        leftLabel: 'Insurance AR',
        value: formatCurrency(totals.insuranceAR.current, { compact: true }),
      },
      {
        id: 'patient-ar',
        leftLabel: 'Patient AR',
        value: formatCurrency(totals.patientAR.current, { compact: true }),
      },
      {
        id: 'overdue-ar',
        leftLabel: 'Overdue AR',
        value: formatCurrency(totals.overdueAR.current, { compact: true }),
      },
    ]
  }, [loading, totals])

  const isEmpty = shouldShowDashboardSectionEmpty(loading, hasData)
  const pending = isDashboardSectionPending(loading, hasPayload)

  return (
    <DashboardSectionCard
      title="AR Aging"
      titleHref={titleHref}
      className="dashboard-finance-ar-card"
      loading={pending}
      skeleton={{ metrics: 4, block: 'chart' }}
      isEmpty={isEmpty}
      disableTitleLink={isEmpty}
    >
      <div className="dashboard-finance-ar">
        <div className="dashboard-finance-ar-columns">
          <div className="dashboard-finance-ar-column dashboard-finance-ar-column-chart">
            <FinanceMetricTilesRow columns={4} metrics={metricTiles} />
            {isEmpty ? (
              <DashboardSectionEmptyState className="dashboard-section-empty--embed" />
            ) : barChartData.length > 0 ? (
              <div className="dashboard-finance-treatment-value-chart-wrap dashboard-finance-bar-chart-legend-spaced">
                <DashboardStackedBarChart
                  data={barChartData}
                  series={[{ dataKey: 'value', name: '', color: '' }]}
                  horizontal={false}
                  stacked={false}
                  categoryIndicatorColors={AR_BUCKET_COLORS}
                  hideCategoryIndicatorCircles
                  hideCategoryAxisLabels
                  valueFormatter={formatChartCurrencyDollars}
                  height={220}
                  yAxisDomain={arValueAxis.yDomain}
                  yAxisTicks={arValueAxis.yTicks}
                  showLegend={false}
                  className="dashboard-finance-ar-total-chart"
                  {...AR_AGING_BAR_CHART_PROPS}
                />
                <FinanceArAgingBarLegend />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </DashboardSectionCard>
  )
}
